import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { addToCart } from "store/slices/cartSlice";
import {
  addToFavorites,
  removeFromFavorites,
} from "store/slices/favoritesSlice";

import api from "api/api";
import endpoint from "api/endpoints";
import { delayCall } from "api/util";
import {
  Loading,
  ErrorMessage,
  ProductImage,
  ShowMoreHtml,
  Dropdown,
  Breadcrumb,
  PreferDeliveryDaySelector,
} from "common";
import {
  getMatrixInfo,
  formatCurrency,
  useQuantityHandlers,
  getCurrentPrice,
  publicItemName,
} from "config/config";

import PurchaseOptions from "../common/ui/PurchaseOptions";
import { unitLabel } from "../components/product/productPresentation";
import RecentlyViewedSection from "../components/sections/RecentlyViewedSection";
import { addToRecentViews } from "../redux/slices/recentViewsSlice";
import { getProductImageUrls } from "../utils/productImages";
import { filterPromotionsByCustomerId } from "../utils/promotionFilterUtil";

import {
  AddToFavorite,
  Button as DsButton,
  Icon,
  PriceTag,
  QuantityStepper,
  Tabs,
} from "@/design-system";

//import { CURRENT_IN_STOCK, OUT_OF_STOCK } from "@/constants/constant";

/* ===== Date helpers (local) ===== */
function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}
function addMonthsSafe(date, months) {
  const d = new Date(date.getTime());
  const day = d.getDate();
  const targetMonth = d.getMonth() + months;
  const targetYear = d.getFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const endDay = daysInMonth(targetYear, normalizedMonth);
  const clamped = Math.min(day, endDay);
  const res = new Date(d);
  res.setFullYear(targetYear, normalizedMonth, clamped);
  return res;
}
function formatLocalDateToronto(date) {
  return date.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "America/Toronto",
  });
}
function nextSubscriptionDateFromToday(intervalStr) {
  const interval = parseInt(intervalStr || "1", 10);
  return addMonthsSafe(new Date(), isNaN(interval) ? 1 : interval);
}

function parseVariantOptionEntries(value) {
  if (!value) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      return parseVariantOptionEntries(JSON.parse(trimmed));
    } catch {
      return [{ name: "Option", value: trimmed }];
    }
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (Array.isArray(entry) && entry.length >= 2) {
          return { name: entry[0], value: entry[1] };
        }
        if (entry && typeof entry === "object") {
          return {
            name: entry.name || entry.label || entry.key,
            value: entry.value,
          };
        }
        return null;
      })
      .filter((entry) => entry?.name && entry.value !== undefined);
  }

  if (typeof value === "object") {
    return Object.entries(value).map(([name, optionValue]) => ({
      name,
      value: optionValue,
    }));
  }

  return [];
}

function getVariantOptionMap(item) {
  return parseVariantOptionEntries(item?.custitem_variant_option_value).reduce(
    (acc, option) => {
      acc[option.name] = String(option.value);
      return acc;
    },
    {}
  );
}

function getVariantAxes(items) {
  for (const item of items) {
    const axes = parseVariantOptionEntries(item?.custitem_variant_option_value)
      .map((option) => option.name)
      .filter(Boolean);
    if (axes.length > 0) return axes;
  }
  return [];
}

function parseJsonValue(value) {
  if (!value || typeof value !== "string") return value;
  try {
    return JSON.parse(value.trim());
  } catch {
    return value;
  }
}

function parseDelimitedTextList(value) {
  if (!value || typeof value !== "string") return [];
  return value
    .split(/\s*\|\s*|\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseKeyValueDetails(value) {
  const parsed = parseJsonValue(value);
  if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
    return [];
  }

  return Object.entries(parsed)
    .map(([label, detail]) => ({
      label,
      detail: String(detail ?? "").trim(),
    }))
    .filter((entry) => entry.label && entry.detail);
}

function parseResourceLinks(value) {
  const parsed = parseJsonValue(value);
  const rawResources = Array.isArray(parsed)
    ? parsed
    : parsed && typeof parsed === "object"
      ? Object.entries(parsed).map(([label, url]) => ({ label, url }))
      : [];

  return rawResources
    .map((resource, index) => {
      if (typeof resource === "string") {
        return { label: `Resource ${index + 1}`, url: resource };
      }
      if (resource && typeof resource === "object") {
        const label = resource.label || resource.name || resource.title;
        const url = resource.url || resource.href || resource.link;
        return { label: label || `Resource ${index + 1}`, url };
      }
      return null;
    })
    .filter((resource) => resource?.label && resource?.url);
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || "").trim());
}

function uniqueById(items) {
  const seen = new Set();
  return items.filter((item) => {
    if (!item?.id || seen.has(String(item.id))) return false;
    seen.add(String(item.id));
    return true;
  });
}

const variantOptionSorter = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

/**
 * The reference PDP's trust row, copied verbatim from its `pdpTrustArr`. The handoff's
 * fidelity bar calls the microcopy "deliberate brand voice", so the labels are not
 * paraphrased — including the en dash in "1–2 day".
 */
const PDP_TRUST = [
  {
    icon: "local_shipping",
    label: "Free 1–2 day delivery on orders over $300",
  },
  {
    icon: "assignment_return",
    label: "30-day hassle-free returns on unopened items",
  },
  { icon: "inventory", label: "Full lot & expiry tracking on every shipment" },
  {
    icon: "verified_user",
    label: "Health Canada licensed — documentation on file",
  },
];

export default function ProductsPage({
  productId: propProductId = null,
  isModal = false,
}) {
  const { id: rawId } = useParams();
  const [modalProductId, setModalProductId] = useState(null);
  const effectiveRawId = modalProductId || propProductId || rawId;

  const favorites = useSelector((s) => s.favorites.items);
  const userInfo = useSelector((s) => s.user.info);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [matrixOptions, setMatrixOptions] = useState([]);
  const [selectedMatrixOption, setSelectedMatrixOption] = useState("");
  const [productGroupVariants, setProductGroupVariants] = useState([]);
  const [variantLoading, setVariantLoading] = useState(false);

  // PDP purchase options
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subInterval, setSubInterval] = useState("1");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const thumbnailListRef = useRef(null);

  // Which of the reference's PDP tabs is showing. The panels the app can fill are
  // Overview / Specifications / Resources; upstream also has Reviews, which has no
  // data source here.
  const [pdpTab, setPdpTab] = useState("overview");

  useEffect(() => {
    setModalProductId(null);
  }, [propProductId, rawId]);

  // Track previous product ID to detect actual product changes
  const prevProductIdRef = useRef(null);

  const { quantity, actualQuantity, handleQuantityChange, setQuantity } =
    useQuantityHandlers(1, product?.stockdescription);

  const pricingInfo = getCurrentPrice(product, quantity, userInfo?.category);
  // eslint-disable-next-line no-unused-vars
  const { basePrice, currentPrice, savePrice } = pricingInfo;
  const aboutThisProductItems = useMemo(
    () => parseDelimitedTextList(product?.custitem_about_this_product),
    [product?.custitem_about_this_product]
  );
  const specificationDetails = useMemo(
    () => parseKeyValueDetails(product?.custitem_specifications),
    [product?.custitem_specifications]
  );
  const additionalResources = useMemo(
    () => parseResourceLinks(product?.custitem_additional_resources),
    [product?.custitem_additional_resources]
  );

  // Reset quantity to 1 only when navigating to a different product
  useEffect(() => {
    if (product?.id && product.id !== prevProductIdRef.current) {
      setQuantity(1);
      prevProductIdRef.current = product.id;
    }
  }, [product?.id, setQuantity]);

  async function resolveProductId(maybeId) {
    const s = String(maybeId || "").trim();
    if (/^\d+$/.test(s)) return s;

    const query = decodeURIComponent(s);
    const candidates = [
      query,
      query.replace(/\s*-\s*.*/, ""),
      query
        .replace(/[^\w\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim(),
    ];

    for (const q of candidates) {
      try {
        const res = await api.post(
          endpoint.POST_GET_ITEMS_BY_NAME({ limit: 1 }),
          { name: q }
        );
        const arr = Array.isArray(res.data)
          ? res.data
          : res.data?.items || res.data;
        const first = Array.isArray(arr) ? arr[0] : undefined;
        if (first?.id) return String(first.id);
      } catch {
        // ignore and try next
      }
    }
    return null;
  }

  // fetch product
  useEffect(() => {
    let abort = false;
    async function fetchProduct() {
      try {
        setLoading(true);
        setError(null);

        const pid = await resolveProductId(effectiveRawId);
        if (!pid) throw new Error("Product not found");

        if (pid !== effectiveRawId && !isModal) {
          navigate(`/product/${pid}`, { replace: true });
        }

        const res = await api.get(endpoint.GET_PRODUCT_BY_ID(pid));
        if (abort) return;

        // Filter promotions based on customer category
        const productData = filterPromotionsByCustomerId(res.data);

        setProduct(productData);
        if (!isModal) dispatch(addToRecentViews(productData.id));

        setIsSubscribed(false);
        setSubInterval("1");
      } catch (err) {
        if (!abort)
          setError(err?.response?.data?.error || "Failed to load product.");
      } finally {
        if (!abort) setLoading(false);
      }
    }
    fetchProduct();
    return () => {
      abort = true;
    };
  }, [effectiveRawId, navigate, dispatch, isModal, userInfo?.category]);

  // variants
  useEffect(() => {
    if (!product?.custitem39) {
      setMatrixOptions([]);
      return;
    }
    async function fetchMatrixOptions() {
      try {
        const res = await api.post(endpoint.POST_GET_PRODUCT_BY_PARENT(), {
          parent: product.custitem39,
        });
        setMatrixOptions(res.data || []);
      } catch {
        setMatrixOptions([]);
      }
    }
    delayCall(fetchMatrixOptions);
  }, [product]);

  useEffect(() => {
    if (product) setSelectedMatrixOption(product.id);
  }, [product]);

  useEffect(() => {
    const groupId = product?.custitem_product_group_id;
    if (!groupId) {
      setProductGroupVariants([]);
      setVariantLoading(false);
      return;
    }

    let abort = false;
    async function fetchProductGroupVariants() {
      try {
        setVariantLoading(true);
        const res = await api.post(endpoint.POST_GET_PRODUCTS_BY_GROUP(), {
          groupId,
        });
        if (!abort) setProductGroupVariants(res.data || []);
      } catch {
        if (!abort) setProductGroupVariants([]);
      } finally {
        if (!abort) setVariantLoading(false);
      }
    }

    fetchProductGroupVariants();
    return () => {
      abort = true;
    };
  }, [product?.custitem_product_group_id]);

  const handleAddToCart = () => {
    if (!product) return;
    const cartItem = {
      id: product.id,
      itemid: product.itemid,
      quantity: Number(actualQuantity),
      userId: userInfo?.id,
      selected: true,
      custrecord_ci_sub_enabled: isSubscribed,
      custrecord_ci_sub_interval: isSubscribed ? subInterval : null,
      custrecord_ci_sub_unit: product.custrecord_ci_sub_unit || "months",
    };
    delayCall(() => {
      dispatch(addToCart(cartItem));
    });
  };

  // ===== Favorites helpers (for the text button) =====
  const isFavorite = product ? favorites.includes(Number(product.id)) : false;

  const toggleFavorite = async () => {
    if (!product) return;
    const itemId = Number(product.id);
    if (isFavorite) {
      dispatch(removeFromFavorites(itemId));
    } else {
      dispatch(addToFavorites(itemId));
    }
  };

  const { matrixType, options: matrixOptionsList } =
    getMatrixInfo(matrixOptions);
  const productGroupVariantItems = useMemo(
    () => uniqueById([product, ...productGroupVariants]),
    [product, productGroupVariants]
  );
  // The gallery is THIS item's images. Until Oct 2026 it merged every group
  // sibling's images (deduped by exact URL), so a group whose variants share
  // one photo under slightly different URLs showed the same picture several
  // times, and an 80-variant group showed 80 thumbnails. Images hang off the
  // item in NetSuite, the variant pickers already navigate to the sibling
  // (whose own page then shows its own photos), and the reference's tiles are
  // the product's. Only when this item has no image at all does the first
  // sibling with one stand in, so the gallery is never empty.
  const productImages = useMemo(() => {
    const own = getProductImageUrls(product);
    if (own.length > 0) return own;
    for (const sibling of productGroupVariants) {
      const urls = getProductImageUrls(sibling);
      if (urls.length > 0) return urls;
    }
    return [];
  }, [product, productGroupVariants]);
  const selectedProductImageIndex =
    productImages.length > 0
      ? Math.min(selectedImageIndex, productImages.length - 1)
      : 0;
  const selectedProductImage =
    productImages[selectedProductImageIndex] || product?.file_url;
  const variantAxes = useMemo(
    () => getVariantAxes(productGroupVariantItems),
    [productGroupVariantItems]
  );
  const selectedVariantOptions = useMemo(
    () => getVariantOptionMap(product),
    [product]
  );
  const variantChoicesByAxis = useMemo(() => {
    return variantAxes.reduce((choices, axis) => {
      const values = new Set();

      productGroupVariantItems.forEach((variant) => {
        const optionMap = getVariantOptionMap(variant);
        const value = optionMap[axis];
        if (value) values.add(value);
      });

      choices[axis] = Array.from(values).sort(variantOptionSorter.compare);
      return choices;
    }, {});
  }, [productGroupVariantItems, variantAxes]);
  const variantChoiceAvailabilityByAxis = useMemo(() => {
    return variantAxes.reduce((availability, axis) => {
      const choices = variantChoicesByAxis[axis] || [];

      availability[axis] = choices.reduce((choiceAvailability, choice) => {
        const nextSelected = { ...selectedVariantOptions, [axis]: choice };
        choiceAvailability[choice] = productGroupVariantItems.some(
          (variant) => {
            const optionMap = getVariantOptionMap(variant);
            return variantAxes.every((variantAxis) => {
              return optionMap[variantAxis] === nextSelected[variantAxis];
            });
          }
        );
        return choiceAvailability;
      }, {});

      return availability;
    }, {});
  }, [
    productGroupVariantItems,
    selectedVariantOptions,
    variantAxes,
    variantChoicesByAxis,
  ]);

  const navigateToProduct = (productId) => {
    if (!productId || String(productId) === String(product?.id)) return;
    if (isModal) {
      setModalProductId(String(productId));
      return;
    }
    navigate(`/product/${productId}`);
  };

  const handleVariantOptionChange = (axis, value) => {
    const nextSelected = { ...selectedVariantOptions, [axis]: value };
    const exactMatch = productGroupVariantItems.find((variant) => {
      const optionMap = getVariantOptionMap(variant);
      return variantAxes.every((variantAxis) => {
        return optionMap[variantAxis] === nextSelected[variantAxis];
      });
    });

    if (exactMatch) {
      navigateToProduct(exactMatch.id);
      return;
    }

    const fallbackMatch = productGroupVariantItems
      .filter((variant) => {
        const optionMap = getVariantOptionMap(variant);
        return optionMap[axis] === value;
      })
      .map((variant) => {
        const optionMap = getVariantOptionMap(variant);
        const preservedAxes = variantAxes.filter((variantAxis) => {
          return (
            variantAxis !== axis &&
            optionMap[variantAxis] === selectedVariantOptions[variantAxis]
          );
        }).length;

        return { variant, preservedAxes };
      })
      .sort((a, b) => b.preservedAxes - a.preservedAxes)[0]?.variant;

    if (fallbackMatch) {
      navigateToProduct(fallbackMatch.id);
    }
  };

  // A new item starts on its first image.
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [product?.id]);

  useEffect(() => {
    const container = thumbnailListRef.current;
    if (!container) return;
    const target = container.children[selectedProductImageIndex];
    if (target && typeof target.scrollIntoView === "function") {
      // The strip is horizontal now, so `inline` is the axis that matters. Both are
      // "nearest" so an already-visible thumbnail scrolls nothing — `scrollIntoView`
      // walks every scrollable ancestor, and a non-nearest value would move the page.
      target.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [selectedProductImageIndex]);

  if (loading) return <Loading text="Loading product..." />;
  if (error) return <ErrorMessage message={error} />;
  if (!product) return null;

  const firstDeliveryDate = isSubscribed
    ? nextSubscriptionDateFromToday(subInterval)
    : null;

  const inStock = Number(product.totalquantityonhand) > 0;

  // The reference's PDP puts long-form content behind tabs. Only offer the ones this
  // product can actually fill; upstream's fourth tab (Reviews) has no data source here.
  const pdpTabItems = [
    (product.storedetaileddescription || aboutThisProductItems.length > 0) && {
      value: "overview",
      label: "Overview",
    },
    specificationDetails.length > 0 && {
      value: "specs",
      label: "Specifications",
    },
    additionalResources.length > 0 && {
      value: "resources",
      label: "Resources",
    },
  ].filter(Boolean);

  // Derived rather than stored: navigating to a product whose tab set no longer holds
  // the remembered tab falls back to the first one it does have, without an effect
  // that would paint the empty panel first.
  const activePdpTab = pdpTabItems.some((tab) => tab.value === pdpTab)
    ? pdpTab
    : pdpTabItems[0]?.value;

  return (
    <>
      <div
        className={
          isModal
            ? ""
            : "mx-auto my-0 max-w-container px-[var(--gutter)] pb-[84px] pt-[var(--space-2)]"
        }
      >
        {!isModal && (
          <Breadcrumb
            // The reference's trail ends on the brand (`cp.brand`) and never shows the
            // product name — an `itemid` here is a 90-character string that wraps the
            // row. Products with no `manufacturer` simply end the trail at Products.
            // No `className`: the component's own `mb-[var(--space-2)]` is already the
            // reference's spacing, and a second `mb-*` would conflict with it.
            path={[
              "Home",
              "Products",
              ...(product.manufacturer ? [product.manufacturer] : []),
            ]}
          />
        )}

        <div className="grid grid-cols-1 gap-[var(--space-8)] md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-[var(--space-12)]">
          {/* ===== Gallery ===== */}
          <div className="md:col-start-1 md:row-start-1">
            <div className="relative flex aspect-[4/3] max-w-[620px] items-center justify-center overflow-hidden rounded-xl border border-line-subtle bg-card shadow-sm">
              <ProductImage
                src={selectedProductImage}
                alt={publicItemName(product.itemid) || "Product image"}
                className={`max-h-full w-full object-contain p-[var(--space-6)] [transition:transform_var(--duration-base)_var(--ease-out)] ${
                  isModal ? "hover:scale-105" : "cursor-zoom-in hover:scale-110"
                }`}
              />

              <AddToFavorite
                saved={isFavorite}
                onToggle={toggleFavorite}
                size="lg"
                className="absolute right-[16px] top-[16px]"
              />
            </div>

            {productImages.length > 1 && (
              <div
                ref={thumbnailListRef}
                className="dnd-thinscroll mt-[14px] flex gap-[var(--space-3)] overflow-x-auto pb-[var(--space-1)]"
              >
                {productImages.map((imageUrl, index) => {
                  const isSelected = index === selectedProductImageIndex;

                  return (
                    <button
                      key={imageUrl}
                      type="button"
                      onClick={() => setSelectedImageIndex(index)}
                      onMouseEnter={() => setSelectedImageIndex(index)}
                      className={`inline-flex h-[76px] w-[76px] shrink-0 cursor-pointer items-center justify-center rounded-md border-[1.5px] bg-card p-[var(--space-1)] [transition:border-color_var(--duration-fast)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                        isSelected
                          ? "border-orange-500"
                          : "border-line-subtle hover:border-orange-500"
                      }`}
                      aria-label={`View product image ${index + 1}`}
                      aria-pressed={isSelected}
                    >
                      <ProductImage
                        src={imageUrl}
                        alt={`${publicItemName(product.itemid) || "Product"} thumbnail ${index + 1}`}
                        className="h-full w-full object-contain"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ===== Buy box ===== */}
          <div className="md:col-start-2 md:row-span-2 md:row-start-1">
            {product.manufacturer && (
              <span className="text-xs font-bold uppercase tracking-[var(--tracking-wider)] text-[var(--text-accent)]">
                {product.manufacturer}
              </span>
            )}

            <h1
              className={`mx-0 mb-[6px] mt-[2px] font-sans font-semibold leading-[1.1] tracking-[-.01em] text-ink ${
                isModal ? "text-xl" : "text-2xl"
              }`}
            >
              {publicItemName(product.itemid)}
            </h1>

            {product.mpn && (
              <div className="mb-[var(--space-2)] flex items-center gap-[var(--space-4)]">
                <span className="text-xs text-ink-3">MPN {product.mpn}</span>
              </div>
            )}

            {/* Price */}
            {product.price ? (
              product.promotioncode_id && product.fixedprice ? (
                <>
                  <div className="mb-[6px] flex items-baseline gap-[var(--space-3)]">
                    <PriceTag
                      amount={Number(product.fixedprice)}
                      original={Number(basePrice)}
                      unit={unitLabel(product)}
                      size={isModal ? "sm" : "md"}
                    />
                  </div>

                  <div className="mx-0 my-[var(--space-2)] flex items-center gap-[10px] rounded-md border border-orange-100 bg-[var(--surface-brand-soft)] px-[14px] py-[var(--space-2)]">
                    <Icon
                      name="sell"
                      size={20}
                      weight={400}
                      fill
                      className="shrink-0 text-orange-500"
                    />
                    <div className="text-sm">
                      <b className="text-orange-800">
                        On promo — {product.promotion_code}.
                      </b>{" "}
                      {product.itemquantifier &&
                      parseInt(product.itemquantifier, 10) > 1 ? (
                        quantity >= parseInt(product.itemquantifier, 10) ? (
                          <span className="text-ink-2">
                            Promo price applied at this quantity.
                          </span>
                        ) : (
                          <span className="text-ink-2">
                            Buy {product.itemquantifier} or more to get{" "}
                            {formatCurrency(product.fixedprice)}.
                          </span>
                        )
                      ) : (
                        <span className="text-ink-2">No minimum quantity.</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="mb-[6px] flex items-baseline gap-[var(--space-3)]">
                  <PriceTag
                    amount={Number(currentPrice)}
                    original={
                      product.pricing &&
                      product.pricing.length > 1 &&
                      Number(currentPrice) < Number(product.price)
                        ? Number(product.price)
                        : undefined
                    }
                    unit={unitLabel(product)}
                    size={isModal ? "sm" : "md"}
                  />
                </div>
              )
            ) : null}

            {/* Stock row. We dropped the "· from our Mississauga warehouse" tail (and a
              delivery estimate) on request as clutter; upstream's Aug 2026 update then
              removed the same tail itself, so plain label-only is now exactly the
              reference's version. */}
            <div className="mb-[9px] flex flex-wrap items-center gap-[var(--space-2)] text-sm font-semibold">
              <Icon
                name={inStock ? "check_circle" : "schedule"}
                size={18}
                weight={400}
                fill
                className={`shrink-0 ${inStock ? "text-success-600" : "text-ink-3"}`}
              />
              <span className={inStock ? "text-success-600" : "text-ink-3"}>
                {inStock
                  ? "In stock — ships today"
                  : "Backordered — we’ll keep you posted"}
              </span>
            </div>

            {/* Quantity-based pricing tiers */}
            {product.pricing && product.pricing.length > 1 && (
              <div className="mb-[var(--space-3)] rounded-lg border border-line-subtle px-[18px] py-[var(--space-4)]">
                <div className="mb-[var(--space-2)] text-xs font-bold uppercase tracking-[var(--tracking-wider)] text-[var(--text-accent)]">
                  Quantity discounts
                </div>
                {product.pricing.map((tier, index) => {
                  const currentQty = parseInt(tier.quantity, 10);
                  const nextTier = product.pricing[index + 1];
                  const nextQty = nextTier
                    ? parseInt(nextTier.quantity, 10)
                    : null;
                  const selectedQty = parseInt(quantity, 10);

                  let rangeText;
                  let isCurrentTier = false;

                  if (index === 0) {
                    rangeText = nextQty ? `1-${nextQty - 1}` : `1+`;
                    isCurrentTier =
                      selectedQty >= 1 &&
                      (nextQty ? selectedQty < nextQty : true);
                  } else if (nextQty) {
                    rangeText = `${currentQty}-${nextQty - 1}`;
                    isCurrentTier =
                      selectedQty >= currentQty && selectedQty < nextQty;
                  } else {
                    rangeText = `${currentQty}+`;
                    isCurrentTier = selectedQty >= currentQty;
                  }

                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between gap-[var(--space-4)] border-b border-line-subtle px-0 py-[10px] text-sm last:border-b-0 ${
                        isCurrentTier ? "font-semibold" : ""
                      }`}
                    >
                      <span
                        className={
                          isCurrentTier ? "text-orange-800" : "text-ink-3"
                        }
                      >
                        Quantity {rangeText}
                        {isCurrentTier && (
                          <span className="ml-[var(--space-1)] text-xs font-normal">
                            (current)
                          </span>
                        )}
                      </span>
                      <b
                        className={`text-right ${
                          isCurrentTier ? "text-orange-800" : "text-ink"
                        }`}
                      >
                        {formatCurrency(tier.price)}
                      </b>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Variants */}
            {variantLoading ? (
              <Loading
                text="Loading variants..."
                size="sm"
                className="mt-[var(--space-4)] items-start py-[var(--space-3)]"
              />
            ) : variantAxes.length > 0 &&
              productGroupVariantItems.length > 1 ? (
              <div className="mb-[var(--space-3)] flex flex-col gap-[15px] rounded-lg border border-line-subtle px-[18px] py-[var(--space-4)]">
                <span className="text-xs font-bold uppercase tracking-[var(--tracking-wider)] text-[var(--text-accent)]">
                  Choose your options
                </span>

                {variantAxes.map((axis) => {
                  const choices = variantChoicesByAxis[axis] || [];
                  const selectedValue = selectedVariantOptions[axis] || "";

                  return (
                    <div key={axis} className="flex flex-col gap-[9px]">
                      <div className="text-sm text-ink-2">
                        {axis}: <b className="text-ink">{selectedValue}</b>
                      </div>
                      <div className="flex flex-wrap gap-[10px]">
                        {choices.map((choice) => {
                          const isSelected = choice === selectedValue;
                          const hasExactCombination =
                            variantChoiceAvailabilityByAxis[axis]?.[choice] !==
                            false;

                          return (
                            <button
                              key={choice}
                              type="button"
                              onClick={() =>
                                handleVariantOptionChange(axis, choice)
                              }
                              className={`inline-flex min-h-[38px] max-w-full cursor-pointer items-center rounded-control border-[1.5px] px-[14px] py-[7px] font-sans text-sm font-medium [transition:border-color_var(--duration-fast),background_var(--duration-fast),color_var(--duration-fast)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                                isSelected
                                  ? "border-blue-600 bg-blue-600 text-white"
                                  : "border-line bg-card text-ink hover:border-orange-500 hover:text-orange-600"
                              } ${
                                !hasExactCombination
                                  ? "line-through decoration-2"
                                  : ""
                              }`}
                              title={
                                hasExactCombination
                                  ? undefined
                                  : "No exact combination; closest option will be selected"
                              }
                              aria-pressed={isSelected}
                            >
                              {choice}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {/* Matrix variants */}
            {matrixOptions.length > 0 && (
              <div className="mb-[var(--space-3)]">
                <Dropdown
                  label={matrixType}
                  options={matrixOptionsList}
                  value={selectedMatrixOption}
                  key={selectedMatrixOption.value}
                  onChange={(e) => {
                    setSelectedMatrixOption(e.target.value);
                    if (e.target.value && e.target.value !== product.id) {
                      navigateToProduct(e.target.value);
                    }
                  }}
                  className="w-48"
                />
              </div>
            )}

            {/* Quantity + add to cart */}
            <div className="mb-[10px] flex flex-wrap items-center gap-[var(--space-3)]">
              <QuantityStepper
                value={Number(quantity) || 1}
                onChange={(value) => handleQuantityChange(product.id, value)}
                min={1}
                size="lg"
              />
              <div className="min-w-[200px] flex-1">
                <DsButton
                  fullWidth
                  size="lg"
                  startIcon="add_shopping_cart"
                  onClick={handleAddToCart}
                >
                  Add to cart
                </DsButton>
              </div>
            </div>

            {product.stockdescription && (
              <div className="mb-[10px] inline-flex items-center gap-[var(--space-2)] rounded-md bg-soft px-[var(--space-3)] py-[6px] text-sm font-medium text-blue-700">
                <Icon name="info" size={16} weight={400} className="shrink-0" />
                {product.stockdescription}
              </div>
            )}

            {actualQuantity > quantity && (
              <div className="mb-[10px] text-sm text-ink-2">
                Selected {quantity} ·{" "}
                <b className="text-orange-700">
                  total with bonus {actualQuantity}
                </b>
              </div>
            )}

            {/* Purchase options */}
            <div className="mb-[var(--space-4)] rounded-lg border border-line-subtle px-[18px] py-[var(--space-4)]">
              <span className="text-xs font-bold uppercase tracking-[var(--tracking-wider)] text-[var(--text-accent)]">
                Purchase options
              </span>

              <PurchaseOptions
                name={`pdp-${product.id}`}
                isSubscribed={isSubscribed}
                interval={subInterval}
                onOneTime={() => {
                  setIsSubscribed(false);
                }}
                onSubscribe={() => {
                  setIsSubscribed(true);
                }}
                onIntervalChange={(val) => setSubInterval(val)}
              />

              {isSubscribed && <PreferDeliveryDaySelector />}

              {isSubscribed && (
                <div className="mt-[var(--space-3)] flex items-center gap-[var(--space-2)] text-xs text-ink-2">
                  <Icon
                    name="event_upcoming"
                    size={16}
                    weight={400}
                    className="shrink-0 text-blue-600"
                  />
                  Next order {formatLocalDateToronto(firstDeliveryDate)} (
                  {subInterval === "1"
                    ? "every month"
                    : `every ${subInterval} months`}
                  )
                </div>
              )}
            </div>

            {/* Trust row — microcopy verbatim from the reference's pdpTrust */}
            <div className="flex flex-col gap-[var(--space-3)] border-t border-line-subtle pt-[18px]">
              {PDP_TRUST.map((item) => (
                <span
                  key={item.label}
                  className="inline-flex items-center gap-[10px] text-sm text-ink-2"
                >
                  <Icon
                    name={item.icon}
                    size={20}
                    weight={400}
                    className="shrink-0 text-blue-600"
                  />
                  {item.label}
                </span>
              ))}
            </div>
          </div>

          {/* ===== Long-form content, tabbed ===== */}
          {pdpTabItems.length > 0 && (
            <div className="md:col-start-1 md:row-start-2">
              {pdpTabItems.length > 1 && (
                <Tabs
                  items={pdpTabItems}
                  value={activePdpTab}
                  onChange={setPdpTab}
                />
              )}

              {activePdpTab === "overview" && (
                <div className="px-0 py-[var(--space-6)] leading-[1.7] text-ink-2">
                  {product.storedetaileddescription && (
                    <div className="first-letter:uppercase">
                      <ShowMoreHtml
                        html={product.storedetaileddescription}
                        maxLength={400}
                      />
                    </div>
                  )}

                  {aboutThisProductItems.length > 0 && (
                    <>
                      <div className="mb-[var(--space-3)] mt-[var(--space-5)] text-xs font-bold uppercase tracking-[var(--tracking-wider)] text-[var(--text-accent)]">
                        About this product
                      </div>
                      <ul className="flex flex-col gap-[var(--space-2)]">
                        {aboutThisProductItems.map((item, index) => (
                          <li
                            key={`${item}-${index}`}
                            className="flex items-start gap-[var(--space-2)] text-sm"
                          >
                            <Icon
                              name="check_circle"
                              size={18}
                              weight={400}
                              fill
                              className="mt-[2px] shrink-0 text-blue-600"
                            />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {activePdpTab === "specs" && (
                <div className="px-0 py-[var(--space-4)]">
                  {specificationDetails.map(({ label, detail }) => (
                    <div
                      key={label}
                      className="flex justify-between gap-[var(--space-4)] border-b border-line-subtle px-0 py-[13px] text-sm"
                    >
                      <span className="text-ink-3">{label}</span>
                      <b className="text-right text-ink">{detail}</b>
                    </div>
                  ))}
                </div>
              )}

              {activePdpTab === "resources" && (
                <div className="grid grid-cols-1 gap-[var(--space-3)] px-0 py-[var(--space-5)] sm:grid-cols-2">
                  {additionalResources.map(({ label, url }) =>
                    isHttpUrl(url) ? (
                      <a
                        key={`${label}-${url}`}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-[var(--space-3)] rounded-md border border-line-subtle bg-card px-[14px] py-[14px] text-left font-sans hover:border-orange-300 hover:shadow-sm"
                      >
                        <span className="inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-sm bg-soft text-blue-600">
                          <Icon name="description" size={22} weight={400} />
                        </span>
                        <span className="min-w-0 flex-1 text-sm font-semibold text-ink">
                          {label}
                        </span>
                        <Icon
                          name="open_in_new"
                          size={20}
                          weight={400}
                          className="shrink-0 text-ink-3"
                        />
                      </a>
                    ) : (
                      <div
                        key={`${label}-${url}`}
                        className="flex items-center gap-[var(--space-3)] rounded-md border border-line-subtle bg-card px-[14px] py-[14px] text-sm text-ink-3"
                      >
                        <span className="inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-sm bg-neutral-100 text-ink-3">
                          <Icon name="description" size={22} weight={400} />
                        </span>
                        <span className="min-w-0 flex-1">{label}</span>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recently viewed sits **outside** the page container, because
          `ProductCarousel` brings its own `max-w-container` — nesting the two would
          re-create the double-container bug. The reference PDP does the same thing: its
          "Clinics also order" rail is a second container after the main one closes. */}
      {!isModal && <RecentlyViewedSection />}
    </>
  );
}
